/**
 * Tilauslistan taulukko
 */

import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileText, Plus, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { buildTrackingLink } from "@/lib/order-helpers";
import type { OrderListItem, OrderTabType } from "@/types/orders";
import { OrderTableRow } from "./OrderTableRow";

interface OrdersTableProps {
  orders: OrderListItem[] | undefined;
  isLoading: boolean;
  activeTab: OrderTabType;
  expandedRows: Set<string>;
  onToggleRow: (orderId: string) => void;
  onEditOrder: (orderId: string) => void;
  onCancelOrder: (order: OrderListItem) => void;
  onCreateOrder: () => void;
  // Pagination
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  loadedCount?: number;
  totalCount?: number;
}

export function OrdersTable({
  orders,
  isLoading,
  activeTab,
  expandedRows,
  onToggleRow,
  onEditOrder,
  onCancelOrder,
  onCreateOrder,
  // Pagination
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  loadedCount = 0,
  totalCount = 0,
}: OrdersTableProps) {
  const handleCopyLink = useCallback(
    (orderNumber: string, trackingToken: string) => {
      const link = buildTrackingLink(orderNumber, trackingToken);
      navigator.clipboard.writeText(link);
      toast.success("Seurantalinkki kopioitu");
    },
    []
  );

  return (
    <div className="space-y-0">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Tilaus</TableHead>
              <TableHead>Asiakas</TableHead>
              <TableHead>Hautausmaa</TableHead>
              <TableHead>Kumppani</TableHead>
              <TableHead>Tila</TableHead>
              <TableHead>Hyväksyntä</TableHead>
              <TableHead>Ikä</TableHead>
              <TableHead className="text-right">Summa</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground">
                  Ladataan...
                </TableCell>
              </TableRow>
            ) : !orders || orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-12">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">Ei tilauksia</h3>
                    <p className="text-muted-foreground mt-1">
                      {activeTab === "active"
                        ? "Luo ensimmäinen tilaus aloittaaksesi."
                        : activeTab === "archive"
                        ? "Arkistossa ei ole vielä tilauksia."
                        : "Ei poistettuja tilauksia."}
                    </p>
                    {activeTab === "active" && (
                      <Button className="mt-4 gap-2" onClick={onCreateOrder}>
                        <Plus className="h-4 w-4" />
                        Luo uusi tilaus
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  isExpanded={expandedRows.has(order.id)}
                  onToggle={() => onToggleRow(order.id)}
                  onEdit={() => onEditOrder(order.id)}
                  onCancel={() => onCancelOrder(order)}
                  onCopyLink={() =>
                    order.tracking_token && handleCopyLink(order.order_number, order.tracking_token)
                  }
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination footer */}
      {orders && orders.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border border-t-0 rounded-b-md bg-muted/30">
          <span className="text-sm text-muted-foreground">
            Näytetään {loadedCount} / {totalCount} tilausta
          </span>
          
          {hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
              className="gap-2"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Ladataan...
                </>
              ) : (
                <>
                  Lataa lisää
                  <ChevronDown className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
